<script setup lang="ts">
// Paginação numerada do Minha Conta V2 (ENG-362): setas nas pontas e
// janela de páginas com reticências (1 2 … N), página ativa com bg primário.
interface Props {
  page: number;
  lastPage: number;
  // resumo "Exibindo X a Y de Z registros" (Figma node 18270:26227) — opcional,
  // só aparece quando o consumidor manda a paginação crua da API (start/end/
  // total), como a de rewards.
  start?: number;
  end?: number;
  total?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  change: [page: number];
}>();

const pages = computed<Array<number | '...'>>(() => {
  const { page, lastPage } = props;

  if (lastPage <= 5) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }

  const candidates = [1, page - 1, page, page + 1, lastPage]
    .filter((p) => p >= 1 && p <= lastPage)
    .filter((p, i, arr) => arr.indexOf(p) === i)
    .sort((a, b) => a - b);

  const result: Array<number | '...'> = [];

  candidates.forEach((p, i) => {
    if (i > 0 && p - candidates[i - 1] > 1) {
      result.push('...');
    }
    result.push(p);
  });

  return result;
});

function goTo(page: number) {
  if (page < 1 || page > props.lastPage || page === props.page) {
    return;
  }

  emit('change', page);
}
</script>

<template>
  <div class="flex w-full shrink-0 flex-col items-center gap-[6px] py-[5px]">
    <nav class="flex w-full items-center justify-between">
      <button
        class="flex size-[24px] items-center justify-center text-white disabled:opacity-30"
        :disabled="props.page <= 1"
        type="button"
        @click="goTo(props.page - 1)"
      >
        <UserV2Icon name="chevron-left" />
      </button>

      <div class="flex items-center gap-[8px]">
        <template
          v-for="(item, index) in pages"
          :key="`${item}-${index}`"
        >
          <span
            v-if="item === '...'"
            class="border-solid flex h-[32px] min-w-[31px] items-center justify-center rounded-[8px] border border-white/10 bg-white/5 px-[8px] text-[12px] leading-[16px] text-white"
          >
            ...
          </span>
          <button
            v-else
            class="flex h-[32px] min-w-[31px] items-center justify-center rounded-[8px] border px-[8px] text-[12px] leading-[16px] text-white"
            :class="
              item === props.page
                ? 'border-v2-primary bg-v2-primary/20'
                : 'border-white/10 bg-white/5'
            "
            type="button"
            @click="goTo(item)"
          >
            {{ item }}
          </button>
        </template>
      </div>

      <button
        class="flex size-[24px] items-center justify-center text-white disabled:opacity-30"
        :disabled="props.page >= props.lastPage"
        type="button"
        @click="goTo(props.page + 1)"
      >
        <UserV2Icon name="chevron-right" />
      </button>
    </nav>

    <p
      v-if="total !== undefined"
      class="text-[8px] leading-[16px] text-white/50"
    >
      Exibindo {{ start }} a {{ end }} de {{ total }} registros
    </p>
  </div>
</template>
